import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './OneOrganization.css';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { addScreenOrganization, getScreens, removeScreenOrganization } from '../../services/screenService';
import { getOneOrganization } from '../../services/Organizations';

function OneOrganization() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { isLoading } = useAuth();
  const [orgData, setOrgData] = useState(null);
  const [orgScreens, setOrgScreens] = useState([]);
  const [allScreens, setAllScreens] = useState([]); 
  const [selectedScreen, setSelectedScreen] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Copy organization ID to clipboard
  const handleCopyId = useCallback(() => {
    if (orgData?.id) {
      navigator.clipboard.writeText(orgData.id).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }, [orgData]);

  // Fetch organization data
  useEffect(() => {
    if (organizationId) {
      setLoading(true);
      getOneOrganization(organizationId)
        .then((data) => {
          setOrgData(data.organization);
          setOrgScreens(data.screens);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching organization:', err);
          setLoading(false);
        });
    }
  }, [organizationId]);

  useEffect(() => {
    getScreens()
      .then((data) => {
        setAllScreens(data);
      })
      .catch((err) => {
        console.error('Error fetching screens:', err);
      });
  }, []);

  const handleAddScreen = useCallback(() => {
    if (selectedScreen) {
      addScreenOrganization(organizationId, selectedScreen)
        .then((response) => {
          const newScreen = response.screen;
          setOrgScreens((prev) => [...prev, { 
            id: response.id,
            created_at: response.created_at,
            password: response.password,
            screen: newScreen 
          }]); 
          setSelectedScreen(''); 
        })
        .catch((err) => {
          console.error('Error adding screen:', err);
        });
    }
  }, [organizationId, selectedScreen]);

  const handleRemoveScreen = useCallback((screenId) => {
    removeScreenOrganization(organizationId, screenId)
      .then(() => {
        setOrgScreens((prev) => prev.filter((screen) => screen.screen.id !== screenId));
      })
      .catch((err) => {
        console.error('Error removing screen from organization:', err);
      });
  }, [organizationId]);

  const availableScreens = useMemo(
    () => allScreens.filter(
      (screen) => !orgScreens.some((orgScreen) => orgScreen.screen.id === screen.id)
    ),
    [allScreens, orgScreens]
  );

  if (loading || isLoading) {
    return (
      <div className="one-org-container">
        <div className="one-org-loading">
          <div className="one-org-loading-spinner"></div>
          <p>Загрузка организации...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="one-org-container">
      {/* Back button */}
      <button className="one-org-back" onClick={() => navigate('/organizations')}>
        ← Назад к организациям
      </button>

      {/* Header */}
      <div className="one-org-header">
        <div className="one-org-header-icon">🏪</div>
        <div className="one-org-header-content">
          <h1 className="one-org-title">{orgData ? orgData.name : 'Загрузка...'}</h1>
          {orgData && orgData.restaurantAddress && (
            <p className="one-org-address">📍 {orgData.restaurantAddress}</p>
          )}
        </div>
      </div>

      {/* Organization ID Section */}
      {orgData && (
        <div className="one-org-id-section">
          <div className="one-org-id-label">🔑 ID Организации (для плагина)</div>
          <div className="one-org-id-box">
            <code className="one-org-id-value">{orgData.id}</code>
            <button 
              className={`one-org-id-copy ${copied ? 'copied' : ''}`}
              onClick={handleCopyId}
              title="Скопировать ID"
            >
              {copied ? '✓ Скопировано' : '📋 Копировать'}
            </button>
          </div>
        </div>
      )}

      {/* Screens Section */}
      <div className="one-org-section">
        <div className="one-org-section-header">
          <h2 className="one-org-section-title">📺 Экраны</h2>
          <span className="one-org-section-count">{orgScreens.length} экранов</span>
        </div>

        {orgScreens.length === 0 ? (
          <div className="one-org-empty">
            <span className="one-org-empty-icon">📺</span>
            <p>Экраны не добавлены</p>
          </div>
        ) : (
          <div className="one-org-screens-list">
            {orgScreens.map((screen) => (
              <div key={screen.screen.id} className="one-org-screen-card">
                <div className="one-org-screen-info">
                  <div className="one-org-screen-icon">🖥️</div>
                  <div className="one-org-screen-details">
                    <strong className="one-org-screen-name">{screen.screen.name}</strong>
                    <a
                      href={`${process.env.REACT_APP_PANEL_URL}/screens/${screen.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="one-org-screen-link"
                    >
                      🔗 Открыть экран
                    </a>
                  </div>
                </div>
                <button
                  className="one-org-screen-remove"
                  onClick={() => handleRemoveScreen(screen.screen.id)}
                  title="Удалить экран"
                >
                  🗑️ Удалить
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Screen Section */}
      <div className="one-org-add-section">
        <h3 className="one-org-add-title">➕ Добавить экран</h3>
        <div className="one-org-add-form">
          <select
            value={selectedScreen}
            onChange={(e) => setSelectedScreen(e.target.value)}
            className="one-org-select"
          >
            <option value="">Выберите экран</option>
            {availableScreens.map((screen) => (
              <option key={screen.id} value={screen.id}>
                {screen.name}
              </option>
            ))}
          </select>
          <button
            className="one-org-add-btn"
            onClick={handleAddScreen}
            disabled={!selectedScreen}
          >
            Добавить
          </button>
        </div>
      </div>
    </div>
  );
}

export default OneOrganization;
